import { useMemo, useState } from 'react';

import { InfoOutlined } from '@mui/icons-material';
import { Box, Stack, Tooltip, Typography } from '@mui/material';

import ExitCriteriaDialog from 'domains/campaigns/components/CustomCampaign/CustomCampaignForm/steps/CustomCampaignScheduleStep/CampaignExitCriteria/ExitCriteriaDialog';
import { Campaign, CampaignChannels, ExitCriteriaFields, SendRecurrenceFrequency } from 'domains/campaigns/types';
import Button from 'domains/core/components/Button';
import DetailItem from 'domains/core/components/DetailItem';
import ErrorSnackbar from 'domains/core/components/Snackbars/ErrorSnackbar';
import SuccessSnackbar from 'domains/core/components/Snackbars/SuccessSnackbar';
import SegmentDetailsDisplay from 'domains/segments/pages/SegmentsPage/SegmentsTable/SegmentDetails/SegmentDetailsDisplay';
import useUpdateCampaignExitCriteria from 'hooks/mutations/useUpdateCampaignExitCriteria';
import useCampaignExitStats from 'hooks/queries/useCampaignExitStats';
import useSegments from 'hooks/queries/useSegments';
import { CampaignStatus } from 'models/enums';
import { getNumberWithCommas } from 'utils';

export type Props = {
    campaign: Campaign;
};

const tooltipMessage =
    'Contacts who match this audience after entering the campaign exit it and receive no further sends. Exited contacts never re-enter.';

const headerStyle = { display: 'flex', alignItems: 'center', marginBottom: '8px', flexDirection: 'row' } as const;
const infoIconStyle = { fontSize: '14px', marginTop: '4px', marginLeft: '4px' };

// Exit criteria only gates future sends, so once the campaign has finished it is read-only.
// Mirrors EXIT_CRITERIA_LOCKED_STATUSES on the backend (PUT /exit-criteria rejects these).
const EXIT_CRITERIA_LOCKED_STATUSES: CampaignStatus[] = [
    CampaignStatus.COMPLETED,
    CampaignStatus.CANCELED,
    CampaignStatus.STOPPED,
    CampaignStatus.FAILED,
    CampaignStatus.DELETED,
    CampaignStatus.WINNER_SENT,
    CampaignStatus.WINNER_FAILED,
    CampaignStatus.WINNER_STOPPED,
];

/**
 * Live-edit surface for a campaign's exit criteria on the campaign details page (post-publish). Shows a
 * read-only summary of the current criteria and how many contacts have exited; all editing happens in a
 * single modal (ExitCriteriaDialog) where the user toggles the feature on/off, edits the rules, and
 * confirms before the change persists via PUT /exit-criteria.
 */
const CampaignExitCriteriaPanel = ({ campaign }: Props) => {
    const [exitCriteria, setExitCriteria] = useState<ExitCriteriaFields | null>(campaign.exitSegmentDefinition ?? null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const {
        mutate: updateExitCriteria,
        isError: isSaveError,
        isSuccess: isSaveSuccess,
    } = useUpdateCampaignExitCriteria(campaign.id);

    const { data: exitStats } = useCampaignExitStats(campaign.id);

    // Segments back the saved-audience dropdown in the modal and resolve the saved-audience name in the
    // summary. Fetched while the modal is open or when the committed criteria references a saved audience.
    const { data: segments, isInitialLoading: isSegmentsLoading } = useSegments({
        enabled: isModalOpen || Boolean(exitCriteria?.segmentId),
    });

    // Surface the campaign's persisted email sends in the exit-criteria engagement send pickers.
    const campaignSends = useMemo(
        () => (campaign.sends ?? []).filter((send) => send.channel === CampaignChannels.EMAIL),
        [campaign.sends]
    );

    // Scope the matching-contact count to the campaign's starting audience. We only have a meaningful
    // starting-audience definition for plain-segment entry audiences — for cohort, A-B, or continual
    // entry (a moving target) we pass null and the count is hidden rather than shown against the whole
    // contact universe. The entry send is the structure root; its snapshot carries the definition.
    const startingAudienceDefinition = useMemo(() => {
        const rootSendId = campaign.structure?.find((item) => item.parentId == null)?.id;
        const entrySend = campaign.sends?.find((send) => send.id === rootSendId) ?? campaign.sends?.[0];
        const entryAudience = campaign.audiences?.find((audience) => audience.parentAudienceId == null);

        const isCohortEntry = Boolean(entrySend?.cohortId || entryAudience?.cohortId);
        const isAbTest = campaign.abTestConfig != null;
        const isContinual =
            campaign.reentryDetails != null || campaign.recurrenceFrequency === SendRecurrenceFrequency.CONTINUOUS;

        if (isCohortEntry || isAbTest || isContinual) return null;
        return entrySend?.segmentDefinition ?? null;
    }, [
        campaign.structure,
        campaign.sends,
        campaign.audiences,
        campaign.abTestConfig,
        campaign.reentryDetails,
        campaign.recurrenceFrequency,
    ]);

    const savedAudienceName = useMemo(() => {
        if (!exitCriteria?.segmentId || !segments) return null;
        return segments.find((segment) => segment.id === exitCriteria.segmentId)?.name ?? null;
    }, [exitCriteria, segments]);

    const exitedCount = exitStats?.exitedCount;
    const isLocked = EXIT_CRITERIA_LOCKED_STATUSES.includes(campaign.status);

    const handleSave = (definition: ExitCriteriaFields | null) => {
        setExitCriteria(definition);
        updateExitCriteria({ exitSegmentDefinition: definition });
        setIsModalOpen(false);
    };

    return (
        <Stack spacing={2} data-testid="campaign-exit-criteria-panel">
            <Stack sx={headerStyle}>
                <Typography variant="body2">Campaign Exit Criteria</Typography>
                <Tooltip title={tooltipMessage}>
                    <InfoOutlined sx={infoIconStyle} aria-label="Exit criteria info" />
                </Tooltip>
            </Stack>

            {exitCriteria ? (
                <Stack spacing={1} data-testid="exit-criteria-summary">
                    {savedAudienceName && (
                        <Typography variant="body2">
                            From audience: <strong>{savedAudienceName}</strong>
                        </Typography>
                    )}
                    <SegmentDetailsDisplay segmentDefinition={exitCriteria} />
                </Stack>
            ) : (
                <Typography variant="body2" color="text.secondary" data-testid="exit-criteria-empty">
                    No exit criteria set.
                </Typography>
            )}

            {!isLocked && (
                <Box>
                    <Button
                        variant="text"
                        color="secondary"
                        onClick={() => setIsModalOpen(true)}
                        data-testid="edit-exit-criteria"
                    >
                        Edit exit criteria
                    </Button>
                </Box>
            )}

            {exitedCount != null && (
                <DetailItem
                    header="Contacts Exited"
                    description={`${getNumberWithCommas(exitedCount)} contacts have exited this Curation`}
                />
            )}

            {isModalOpen && (
                <ExitCriteriaDialog
                    isOpen={isModalOpen}
                    title="Edit exit criteria"
                    showEnableToggle
                    confirmOnSave
                    initialCriteria={exitCriteria}
                    segments={segments}
                    isSegmentsLoading={isSegmentsLoading}
                    campaignSends={campaignSends}
                    startingAudienceDefinition={startingAudienceDefinition}
                    onClose={() => setIsModalOpen(false)}
                    onSave={handleSave}
                />
            )}

            {isSaveSuccess && (
                <SuccessSnackbar data-testid="exit-criteria-success" successMessage="Exit criteria updated." />
            )}
            {isSaveError && <ErrorSnackbar errorMessage="Couldn’t update exit criteria. Please try again." />}
        </Stack>
    );
};

export default CampaignExitCriteriaPanel;
